<script setup lang="ts">
import { computed, useAttrs, type Component } from "vue";
import { resolveIcon, useIconRegistry } from "../icons/iconRegistry";

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<{
  name?: string | Component;
  size?: number | string;
  color?: string;
  strokeWidth?: number;
  label?: string;
  decorative?: boolean;
}>(), {
  size: 16,
  strokeWidth: 2,
  decorative: undefined,
});

const attrs = useAttrs();
const registry = useIconRegistry();

const iconComponent = computed<Component | null>(() => {
  if (!props.name) return null;
  if (typeof props.name !== "string") return props.name;

  const iconName = props.name
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");

  return resolveIcon(iconName, registry) as Component | null;
});

const iconAttrs = computed(() => {
  const labelled = Boolean(props.label || attrs["aria-label"]);
  return {
    ...attrs,
    "aria-label": props.label ?? attrs["aria-label"],
    "aria-hidden": props.decorative === true || (!labelled && attrs["aria-hidden"] === undefined)
      ? "true"
      : attrs["aria-hidden"],
    role: labelled ? (attrs.role ?? "img") : attrs.role,
  };
});
</script>

<template>
  <component
    :is="iconComponent"
    v-if="iconComponent"
    :size="size"
    :color="color"
    :stroke-width="strokeWidth"
    v-bind="iconAttrs"
    class="luna-icon"
  />
</template>

<style scoped>
.luna-icon {
  display: inline-block;
  vertical-align: middle;
}
</style>
